import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { invoke } from '@tauri-apps/api/core';
import { KeyRound, ShieldAlert } from 'lucide-react';
import { isTauri } from '../utils/tauri';
import { subscribeTauriEvent } from '../utils/safeTauriEvent';
import { refreshVaultMaster } from '../store/vaultStore';
import VaultPassphraseInput from './VaultPassphraseInput';
import { UNLOCK_PASSPHRASE_ERROR } from './vaultPassphraseCopy';
import { adoptRekey, rekeyErrorText } from './vaultRekey';
import './Vault.css';

const RECHECK_MS = 60 * 1000;

/**
 * The master passphrase was changed on another device (Standalone vault card
 * 2.5). Reads `vault_sync_status.rekey`:
 *
 * - `"verified"` — the new keyblock carries a proof only a holder of THIS
 *   computer's key could make. Ask for the new passphrase; adopting it
 *   re-encrypts this computer's copy and keeps every unsynced edit.
 * - `"unverified"` — no such proof. A security warning and NO password field
 *   (card R2.1: a forged keyblock must never earn a "type your new password"
 *   prompt). Nothing on this computer changed.
 * - `"pending"` (locked) — nothing here; the unlock screen takes either
 *   passphrase and adopts on its own.
 */
export default function VaultRekeyElsewhereBanner() {
  const { t } = useTranslation();
  const [state, setState] = useState(null);
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const read = useCallback(async () => {
    try {
      const status = await invoke('vault_sync_status');
      setState(status?.rekey || null);
    } catch {
      // A failed read says nothing about the key; keep the last answer.
    }
  }, []);

  useEffect(() => {
    if (!isTauri()) return undefined;
    read();
    const timer = setInterval(read, RECHECK_MS);
    window.addEventListener('vault-master-changed', read);
    const subs = ['vault:changed', 'vault:master-changed'].map((e) => subscribeTauriEvent(e, read));
    return () => {
      clearInterval(timer);
      window.removeEventListener('vault-master-changed', read);
      subs.forEach((s) => s?.unlisten?.());
    };
  }, [read]);

  const adopt = async () => {
    if (busy || !secret) return;
    setBusy(true);
    setError('');
    try {
      await adoptRekey(secret);
      setSecret('');
      setState(null);
      await refreshVaultMaster();
      window.dispatchEvent(new Event('vault-master-changed'));
    } catch (e) {
      setError(rekeyErrorText(t, e) || t('vault.unlockFailed', UNLOCK_PASSPHRASE_ERROR));
    }
    setBusy(false);
  };

  if (!isTauri() || (state !== 'verified' && state !== 'unverified')) return null;

  if (state === 'unverified') {
    return (
      <div className="vault-pass-banner vault-pass-banner--warn" data-testid="vault-rekey-unverified">
        <ShieldAlert size={15} className="vault-pass-banner-icon" />
        <div className="vault-pass-banner-texts">
          <span className="vault-pass-banner-title">
            {t('vault.rekeyUnverifiedTitle', 'A change to your vault key could not be verified')}
          </span>
          <span className="vault-pass-banner-text">
            {t(
              'vault.rekeyUnverifiedBody',
              'It did not come from one of your devices, so it was not used. Your vault on this computer is unchanged and keeps working. If you did not change your master passphrase, change your account password and turn on two-step sign-in.'
            )}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="vault-pass-banner" data-testid="vault-rekey-verified">
      <KeyRound size={15} className="vault-pass-banner-icon" />
      <div className="vault-pass-banner-texts">
        <span className="vault-pass-banner-title">
          {t('vault.rekeyElsewhereTitle', 'Your master passphrase was changed on another device')}
        </span>
        <span className="vault-pass-banner-text">
          {t(
            'vault.rekeyElsewhereBody',
            'Enter the new passphrase to keep this computer in sync. Nothing is lost meanwhile: changes made here wait and are sent once it is entered.'
          )}
        </span>
        <VaultPassphraseInput
          id="vault-rekey-elsewhere-secret"
          label={t('vault.rekeyElsewhereLabel', 'New master passphrase (or the new 12 words)')}
          value={secret}
          onChange={setSecret}
          autoComplete="current-password"
          disabled={busy}
          onKeyDown={(e) => {
            if (e.key === 'Enter') adopt();
          }}
        />
        {error && <span className="vault-modal-error">{error}</span>}
      </div>
      <div className="vault-pass-banner-actions">
        <button
          type="button"
          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors modern-button text-sm"
          onClick={adopt}
          disabled={busy || !secret}
        >
          {busy ? t('vault.rekeyElsewhereBusy', 'Updating…') : t('vault.rekeyElsewhereAction', 'Use new passphrase')}
        </button>
      </div>
    </div>
  );
}
